import React from 'react';
import { FileText, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { getDocumentDisplayConfig } from '../../../lib/statusHelpers';

export const DocumentsTab = ({ clientDocs, loadingDocs, onView }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-amber-500" />
        Agreements & Documents
      </CardTitle>
    </CardHeader>
    <CardContent>
      {loadingDocs ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
        </div>
      ) : clientDocs.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No documents available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Alert banners */}
          {clientDocs.filter(d => d.status === 'pending_acknowledgment' || d.status === 'expired').length > 0 && (
            <div className="space-y-2 mb-4">
              {clientDocs.filter(d => d.status === 'pending_acknowledgment').length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-800">
                      You have <strong>{clientDocs.filter(d => d.status === 'pending_acknowledgment').length}</strong> document(s) requiring your review and acknowledgment.
                    </p>
                  </div>
                </div>
              )}
              {clientDocs.filter(d => d.is_expired).length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-800">
                      <strong>{clientDocs.filter(d => d.is_expired).length}</strong> document(s) have expired and require renewal.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {clientDocs.map((doc) => {
            const { isExpired, borderClass, iconBg, iconColor, StatusIcon } = getDocumentDisplayConfig(doc);
            const statusIconColor = iconColor;

            return (
            <div
              key={doc.id}
              className={`p-4 rounded-lg border cursor-pointer transition-colors hover:shadow-sm ${borderClass}`}
              onClick={() => onView(doc)}
              data-testid={`doc-item-${doc.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`p-1.5 rounded-md mt-0.5 ${iconBg}`}>
                    <StatusIcon className={`h-4 w-4 ${statusIconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {doc.is_addendum && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Addendum</Badge>
                      )}
                      <Badge className={`text-xs ${
                        doc.template_type === 'terms_and_conditions'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {doc.template_type === 'terms_and_conditions' ? 'Terms & Conditions' : doc.template_type.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {doc.addendum_summary && (
                      <p className="text-xs text-gray-500 mt-1 italic">{doc.addendum_summary}</p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {isExpired ? (
                    <div>
                      <Badge className="bg-red-100 text-red-700 text-xs">Expired</Badge>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(doc.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  ) : doc.status === 'acknowledged' ? (
                    <div>
                      <Badge className="bg-green-100 text-green-700 text-xs">Acknowledged</Badge>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(doc.acknowledged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      {doc.expires_at && (
                        <p className="text-xs text-blue-500 mt-0.5">
                          Expires {new Date(doc.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Action Required</Badge>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);
